import {AppRouterOutput} from "backoffice/api/AppRouter";
import {useTRPC} from "backoffice/ui/App.tsx";
import {Footer, FormCol, Pager, TextDiv, useLoader, useMessage} from "backoffice/ui/Components";
import {FC, useEffect, useState} from "react";
import {Breadcrumb, Button, Col, Container, Form, FormControl, Modal, Row, Table} from "react-bootstrap";
import {Helmet} from "react-helmet";
import {useSearchParams} from "react-router-dom";
import {useImmerChangeHandler} from "src/utils/react/hooks";
import {useImmer} from "use-immer";

const pageSize = 50;

const defaultCondition = {
  fundId   : "",
  loanprjId: "",
};

type ResultType = AppRouterOutput["fund"]["transfer"]["list"];

export const FundTransferPage: FC = () => {
  const Loader  = useLoader();
  const Message = useMessage();
  const tRPC    = useTRPC();

  const [query, setQuery]         = useSearchParams();
  const [condition, setCondition] = useImmer(defaultCondition);
  const [result, setResult]       = useState<ResultType>({
    total: 0,
    list : [],
    page : 0,
    pageSize,
  });
  const [selection, setSelection] = useState<string[]>([]);
  const [modal, setModal]         = useState<string>("");
  const [date, setDate]           = useState<string>("");
  const [keyword, setKeyword]     = useState<string>("");

  // 初期化
  useEffect(() => {
    const cond = Object.entries(defaultCondition).reduce((cond, [k, v]) => {
      cond[k as keyof typeof defaultCondition] = query.get(k) || v;
      return cond;
    }, {} as typeof defaultCondition);
    const page = Number(query.get("page")) || 1;

    setCondition(cond);

    // データ取得
    Loader.task(() => tRPC.fund.transfer.list.query({...cond, page, pageSize}), 300)
          .then((result) => setResult(result))
          .catch((err) => {
            console.error(err);
            Message.error(err);
          });
  }, [query]);

  // 検索条件 編集
  const handleChange = useImmerChangeHandler(setCondition);

  // 検索
  const handleSearch = () => {
    for (const [k, v] of Object.entries(condition)) {
      if (v.trim()) {
        query.set(k, v);
      } else {
        query.delete(k);
      }
    }
    handlePage(1);
  };

  // ページ遷移
  const handlePage = (page: number) => {
    query.set("page", String(page));
    query.set("ts", String(new Date().getTime()));
    setQuery(query);
  };

  const handleSubmit = () => {
    Loader.task(() => tRPC.fund.transfer.update.mutate({date, funds: selection}), 300)
          .then(() => {
            const list = result.list.map((fund) => {
              if (selection.includes(fund.item_id)) {
                fund.transfer_date = date;
              }
              return fund;
            });
            setResult({...result, list});
            setSelection([]);
            setModal("");
          })
          .catch((err) => {
            console.error(err);
            Message.error(err);
          });
  };

  return (
      <>
        <Helmet>
          <title>資金移動日</title>
        </Helmet>

        <div className={"container"}>
          <Breadcrumb>
            <Breadcrumb.Item active>ファンド管理</Breadcrumb.Item>
            <Breadcrumb.Item active>資金移動日</Breadcrumb.Item>
          </Breadcrumb>
        </div>

        <Container>
          <Row>
            <Col xl={{offset: 2, span: 8}}>
              <Form className={"my-5"}>
                <input type="submit" className={"d-none"} disabled/>
                <Row>
                  <FormCol inputId={"fundId"}
                           className={"py-1"}
                           labelColClassname="text-end"
                           title={"ファンドID"}
                           size={{xs: [4, 8], md: [2, 4]}}>
                    <FormControl name={"fundId"}
                                 value={condition.fundId}
                                 onChange={handleChange}/>
                  </FormCol>

                  <FormCol inputId={"lonaprjId"}
                           className={"py-1"}
                           labelColClassname="text-end"
                           title={"融資案件ID"}
                           size={{xs: [4, 8], md: [2, 4]}}>
                    <FormControl name={"loanprjId"}
                                 value={condition.loanprjId}
                                 onChange={handleChange}/>
                  </FormCol>
                </Row>

                <Row style={{marginTop: 20}}>
                  <Col md={{span: 6, offset: 3}} style={{padding: 10}}>
                    <Button variant="primary"
                            className={"w-100"}
                            onClick={handleSearch}>
                      検索する
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </Container>

        <div className={"container small"}>
          <Table hover>
            <thead>
            <tr>
              <th
                  className="text-center align-middle"
                  style={{
                    cursor: "pointer",
                  }}
                  onClick={() => setSelection(selection.length === result.list.length ? [] : result.list.map((el) => el.item_id))}
              >
                <input type="checkbox" checked={selection.length === result.list.length}/>
              </th>
              <th className="text-center">ファンドID</th>
              <th className="text-start ">ファンド名</th>
              <th className="text-end   ">主案件ID</th>
              <th className="text-start ">主案件</th>
              <th className="text-center">資金移動日</th>
              <th className="text-center">運用開始日</th>
              <th className="text-end   ">運用可能額</th>
            </tr>
            </thead>

            <tbody>
            {result.list.length === 0 && (
                <tr>
                  <td colSpan={7}>no data</td>
                </tr>
            )}
            {result.list.map((fund) => (
                <tr key={`${fund.item_id}`}>
                  <td className="text-center align-middle"
                      style={{cursor: "pointer"}}
                      onClick={() => setSelection(selection.toggle(fund.item_id))}>
                    <input type="checkbox" checked={selection.includes(fund.item_id)}/>
                  </td>
                  <td className={"text-center"}>{fund.item_id}</td>
                  <td className={"text-start "}>{fund.item_name}</td>
                  <td className={"text-end"}>{fund.loanprj_id ?? "-"}</td>
                  <td className={"text-start "}><TextDiv width={"7rem"}>{fund.project_name ?? "-"}</TextDiv></td>
                  <td className={"text-center"}>{fund.transfer_date ?? "--"}</td>
                  <td className={"text-center"}>{fund.activate_date}</td>
                  <td className={"text-end   "}>{fund.establishedAmount.toNumber().toLocaleString()}</td>
                </tr>
            ))}
            </tbody>
          </Table>

          <div className={"d-flex justify-content-end"}>
            <Pager activePage={Number(query.get("page")) || 1}
                   pageSize={pageSize}
                   totalCount={Number(result.total)}
                   displaySize={2}
                   onChange={({page}) => handlePage(page)}/>
          </div>
        </div>

        <Footer>
          <Button variant="primary"
                  className={"m-2"}
                  style={{width: 200}}
                  size={"sm"}
                  disabled={selection.length === 0}
                  onClick={() => {
                    setModal("input");
                    setDate("");
                    setKeyword("");
                  }}>
            資金移動日入力
          </Button>
        </Footer>

        <Modal show={modal === "input"}>
          <Modal.Body>
            <Row className={"pb-3"}>
              <FormCol inputId={"date"}
                       className={"py-1"}
                       labelColClassname="text-end"
                       title={"資金移動日"}
                       size={{xs: [4, 8]}}>
                <FormControl type="date"
                             value={date}
                             onChange={(event) => setDate(event.currentTarget.value)}/>
              </FormCol>
            </Row>
            <Row className={"pb-3"}>
              <FormCol inputId={"keyword"}
                       className={"py-1"}
                       labelColClassname="text-end"
                       title={"キーワード"}
                       size={{xs: [4, 8]}}>
                <FormControl value={keyword}
                             onChange={(event) => setKeyword(event.currentTarget.value)} placeholder="資金移動日更新"/>
              </FormCol>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="danger"
                    onClick={() => setModal("")}
                    style={{minWidth: "10em"}}>
              キャンセル
            </Button>

            <Button variant="primary"
                    onClick={() => handleSubmit()} style={{minWidth: "10em"}}
                    disabled={date === "" || keyword !== "資金移動日更新"}>
              登録
            </Button>
          </Modal.Footer>
        </Modal>
      </>
  );
};
